import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Download,
  MapPinOff,
  MountainSnow,
  Landmark as LandmarkIcon,
  Route as RouteIcon,
} from "lucide-react";
import type { ActivityShareData } from "@atlas/shared";
import { fetchActivityShare } from "../api/activities";
import { ActivityRouteMap, type RoutePinClick } from "../components/map/ActivityRouteMap";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { FeatureSummaryCard, type SelectedFeature } from "../components/FeatureSummaryCard";
import { PoweredByStrava, stravaActivityUrl } from "../components/StravaBrand";
import { usePageMeta } from "../hooks/usePageMeta";
import { API_BASE_URL } from "../lib/api-client";
import { applySiteTheme } from "../lib/theme";

// Public activity page behind a signed share link (bare root route, no shell).
// Full-screen route map with a glass panel: title, date, stats, what was
// collected, optional sharer identity, and the single gold "Join on Atlas"
// CTA. Crawlers never reach this page (the API landing serves them OG tags
// and 302s browsers here), so meta is client-side only.

function formatDistance(m: number | null): string | null {
  if (m == null) return null;
  const km = m / 1000;
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
}

function formatElevation(m: number | null): string | null {
  return m == null ? null : `${Math.round(m).toLocaleString("en-GB")} m`;
}

function formatDuration(s: number | null): string | null {
  if (s == null || s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ font: "var(--type-stat, 700 26px/1.05 var(--font-display))", fontSize: 26, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em" }}>
        {value}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 4 }}>
        {label}
      </span>
    </div>
  );
}

function Chip({
  children,
  tone,
  href,
  onClick,
}: {
  children: React.ReactNode;
  tone: "gold" | "sky" | "spruce";
  href?: string;
  onClick?: () => void;
}) {
  const dot = tone === "gold" ? "var(--gold-400)" : tone === "sky" ? "var(--status-info)" : "var(--status-success)";
  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 10px",
    borderRadius: "var(--radius-pill)",
    background: "var(--surface-raised)",
    border: "1px solid var(--border-subtle)",
    color: "var(--text-primary)",
    fontSize: 12.5,
    fontWeight: 600,
    textDecoration: "none",
    maxWidth: "100%",
  };
  const inner = (
    <>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: dot, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...style, cursor: "pointer", font: "inherit", fontSize: 12.5, fontWeight: 600 }}
      >
        {inner}
      </button>
    );
  }
  return href ? (
    <a href={href} style={style}>
      {inner}
    </a>
  ) : (
    <span style={style}>{inner}</span>
  );
}


function Panel({
  data,
  gpxUrl,
  onSelect,
}: {
  data: ActivityShareData;
  gpxUrl: string;
  onSelect: (feature: SelectedFeature) => void;
}) {
  const { activity, owner } = data;
  const date = new Date(activity.startedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const stats: Array<[string, string]> = [];
  const dist = formatDistance(activity.distanceM);
  const elev = formatElevation(activity.elevationGainM);
  const dur = formatDuration(activity.durationSeconds);
  if (dist) stats.push([dist, "Distance"]);
  if (elev) stats.push([elev, "Elevation"]);
  if (dur) stats.push([dur, "Time"]);
  const hasCollected = data.peaks.length + data.landmarks.length + data.trails.length > 0;

  return (
    <div
      className="atlas-glass activity-share-panel"
      style={{
        position: "absolute",
        left: 16,
        top: 16,
        zIndex: 10,
        width: 380,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-lg)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <img src="/atlas-logo.svg" alt="Atlas" height={26} style={{ display: "block" }} />
        </a>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: ".08em", color: "var(--text-secondary)" }}>{date}</span>
      </div>

      <div>
        <h1 style={{ font: "var(--type-display)", fontSize: 24, lineHeight: 1.15, letterSpacing: "-.02em", color: "var(--text-primary)", margin: 0, overflowWrap: "anywhere" }}>
          {activity.name}
        </h1>
        {owner && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            {owner.avatarUrl ? (
              <img src={owner.avatarUrl} alt="" width={30} height={30} style={{ borderRadius: 999, objectFit: "cover", border: "2px solid var(--gold-500)" }} />
            ) : (
              <span style={{ width: 30, height: 30, borderRadius: 999, background: "var(--surface-raised)", border: "2px solid var(--gold-500)", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
                {owner.name.split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase() || "A"}
              </span>
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
              {owner.username ? <a href={`/explorers/${owner.username}`} style={{ color: "inherit", textDecoration: "none" }}>{owner.name}</a> : owner.name}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, letterSpacing: ".06em", padding: "2px 8px", borderRadius: 999, background: "var(--gold-500)", color: "var(--text-on-gold)" }}>
              LVL {owner.level}
            </span>
          </div>
        )}
      </div>

      {stats.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: 12 }}>
          {stats.map(([v, l]) => (
            <Stat key={l} value={v} label={l} />
          ))}
        </div>
      )}

      {hasCollected && (
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
            Reached on this outing
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {data.peaks.map((p) => (
              <Chip key={p.id} tone="gold" onClick={() => onSelect({ kind: "peak", slug: p.slug, name: p.name })}>
                <MountainSnow size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {p.name}
                {p.elevationM != null ? ` · ${Math.round(p.elevationM)} m` : ""}
                {p.isNew ? " · new" : ""}
              </Chip>
            ))}
            {data.landmarks.map((l) => (
              <Chip key={l.id} tone="sky" onClick={() => onSelect({ kind: "landmark", slug: l.slug, name: l.name })}>
                <LandmarkIcon size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {l.name}
                {l.isNew ? " · new" : ""}
              </Chip>
            ))}
            {data.trails.map((t) => (
              <Chip key={t.id} tone="spruce" onClick={() => onSelect({ kind: "trail", slug: t.slug, name: t.name })}>
                <RouteIcon size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {t.name}
                {t.sectionsCount > 0 ? ` · ${t.sectionsCount} ${t.sectionsCount === 1 ? "section" : "sections"}` : ""}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {activity.geojson && (
        <a
          href={gpxUrl}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--text-secondary)",
            textDecoration: "none",
          }}
        >
          <Download size={13} />
          Download route as GPX
        </a>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <Button
          onClick={() => {
            window.location.href = "/signup";
          }}
        >
          Join on Atlas
        </Button>
        {activity.sourceType === "strava" && activity.sourceId && (
          <a
            href={stravaActivityUrl(activity.sourceId)}
            target="_blank"
            rel="noreferrer"
            title="View on Strava"
            style={{ display: "inline-flex", lineHeight: 0 }}
          >
            <PoweredByStrava height={30} />
          </a>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
        Atlas turns your GPS tracks into a record of every peak, trail and landmark you&#39;ve reached.
      </p>
    </div>
  );
}

export default function ActivitySharePage({ id, sig, identity }: { id: string; sig?: string; identity: boolean }) {
  const hasLink = !!sig;
  const query = useQuery({
    queryKey: ["activity-share", id, sig, identity],
    queryFn: () => fetchActivityShare(id, sig!, identity),
    enabled: hasLink,
    staleTime: 60_000,
    retry: 1,
  });
  const data = query.data;
  const [wide] = React.useState(() => typeof window === "undefined" || window.innerWidth > 760);
  const [selected, setSelected] = React.useState<SelectedFeature | null>(null);
  const gpxUrl = React.useMemo(() => {
    const params = new URLSearchParams({ sig: sig ?? "" });
    if (identity) params.set("i", "1");
    return `${API_BASE_URL}/share/activity/${id}.gpx?${params}`;
  }, [id, sig, identity]);
  const onPinClick = React.useCallback((pin: RoutePinClick) => {
    setSelected({ kind: pin.kind, slug: pin.slug, name: pin.name });
  }, []);

  usePageMeta({
    title: data ? `${data.activity.name} — on Atlas` : "Shared activity",
    description: data
      ? `${data.owner ? data.owner.name : "An Atlas explorer"} logged ${data.activity.name} on Atlas.`
      : "A shared activity on Atlas.",
    canonicalPath: `/share/activity/${id}`,
    enabled: !!data,
  });

  React.useEffect(() => {
    applySiteTheme("dark");
  }, []);

  if (!hasLink || query.isError) {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center", background: "var(--bg-app)", padding: 20 }}>
        <EmptyState
          icon={<MapPinOff size={20} />}
          title="This share link isn't valid"
          hint="Ask the explorer who shared it for a fresh link."
        />
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "100vh", overflow: "hidden", background: "var(--bg-app)" }}>
      <style>{`
        @media (max-width: 760px) {
          .activity-share-panel { left: 8px !important; right: 8px !important; top: auto !important; bottom: 8px !important;
            width: auto !important; max-width: none !important; max-height: 62vh !important; padding: 16px !important; }
        }
      `}</style>
      {data && (
        <ActivityRouteMap
          data={data}
          // Keep the route clear of the panel: left rail on wide screens,
          // bottom sheet on narrow ones (mirrors the media query above).
          padding={
            wide
              ? { top: 48, right: 48, bottom: 48, left: 440 }
              : { top: 32, right: 32, bottom: Math.round(window.innerHeight * 0.62) + 24, left: 32 }
          }
          interactive
          onPinClick={onPinClick}
        />
      )}
      {data && <Panel data={data} gpxUrl={gpxUrl} onSelect={setSelected} />}
      {data && selected && <FeatureSummaryCard selected={selected} onClose={() => setSelected(null)} />}
      {!data && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Loading route…
        </div>
      )}
    </div>
  );
}
